import { TOTAL_WEEKS, STATIC_COURSE } from "../content/course_static";
import { completeWeek as completeCourseWeek, uncompleteWeek, loadProgress, saveProgress } from "./progress";

const KEY = "nofap27_weektasks_v1";
const DAYS_PER_WEEK = 7;

function getUserTimeZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
}

function datePartsInTZ(ts, tz) {
  const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" });
  const parts = fmt.formatToParts(new Date(ts));
  const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
  return { y: Number(map.year), m: Number(map.month), d: Number(map.day) };
}

function dateKeyFromParts({ y, m, d }) {
  const mm = String(m).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

function keyToUTCDate(key) {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function daysBetweenKeys(a, b) {
  const A = keyToUTCDate(a).getTime();
  const B = keyToUTCDate(b).getTime();
  return Math.round((B - A) / (24 * 60 * 60 * 1000));
}

export function todayKey(tz = getUserTimeZone(), ts = Date.now()) {
  return dateKeyFromParts(datePartsInTZ(ts, tz));
}

function defaultState() {
  return {
    tz: getUserTimeZone(),
    weeks: {}, // { [weekNumber]: { startDate, checks[7], completeOn, resetCount } }
    lastSeenMs: Date.now(),
    updatedAt: Date.now(),
  };
}

function currentDateKey(state) {
  const now = Date.now();
  const ms = Math.max(now, state.lastSeenMs || now);
  state.lastSeenMs = ms;
  return dateKeyFromParts(datePartsInTZ(ms, state.tz));
}

export function loadTasks() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return defaultState();
    const data = JSON.parse(raw);
    return { ...defaultState(), ...data };
  } catch (e) {
    console.error("Failed to parse tasks; resetting", e);
    return defaultState();
  }
}

function broadcastTasksChanged() {
  try {
    if (typeof window !== "undefined") {
      const ev = new CustomEvent("rl_tasks_changed", { detail: { at: Date.now() } });
      window.dispatchEvent(ev);
    }
  } catch {}
}

function saveTasks(state) {
  const toSave = { ...state, tz: getUserTimeZone(), updatedAt: Date.now() };
  localStorage.setItem(KEY, JSON.stringify(toSave));
  // notify listeners across the app
  broadcastTasksChanged();
  return toSave;
}

function ensureWeekState(state, week) {
  if (!state.weeks[week]) {
    state.weeks[week] = {
      startDate: todayKey(state.tz),
      checks: Array(DAYS_PER_WEEK).fill(false),
      completeOn: null,
      resetCount: 0,
      lastResetOn: null,
    };
    return true; // created new week state
  }
  return false;
}

export function firstIncompleteWeek() {
  const s = loadProgress();
  for (let w = 1; w <= TOTAL_WEEKS; w++) {
    const isCompleted = s.completedWeeks.includes(w);
    const isSkipped = s.skippedWeeks && s.skippedWeeks.includes(w);
    if (!isCompleted && !isSkipped) return w;
  }
  return TOTAL_WEEKS; // all completed or skipped
}

// Resets week if a day was missed when the date advanced.
function applyRolloverRules(state, week) {
  let changed = ensureWeekState(state, week);
  const w = state.weeks[week];
  const today = currentDateKey(state);
  // Calculate which day index we should be on based on startDate
  const delta = Math.max(0, daysBetweenKeys(w.startDate, today));
  if (delta === 0) return changed; // still same day, return whether created
  // We advanced at least 1 day. If the previous day wasn't completed, reset.
  const prevDayIndex = Math.min(delta, DAYS_PER_WEEK) - 1; // index for the last completed calendar day
  if (!w.checks[prevDayIndex]) {
    // Reset: start today, clear checks, clear completion
    w.startDate = today;
    w.checks = Array(DAYS_PER_WEEK).fill(false);
    w.completeOn = null;
    w.resetCount = (w.resetCount || 0) + 1;
    w.lastResetOn = today;
    changed = true;
  } else {
    // If it was completed, keep state; if we jumped several days, any missing day implies reset too
    let ok = true;
    for (let i = 0; i <= prevDayIndex; i++) if (!w.checks[i]) { ok = false; break; }
    if (!ok) {
      w.startDate = today;
      w.checks = Array(DAYS_PER_WEEK).fill(false);
      w.completeOn = null;
      w.resetCount = (w.resetCount || 0) + 1;
      w.lastResetOn = today;
      changed = true;
    }
  }
  return changed;
}

export function getWeekTaskState(week) {
  const tasks = loadTasks();
  const changed = applyRolloverRules(tasks, week);
  return changed ? saveTasks(tasks) : tasks;
}

export function getCurrentDayIndex(week) {
  const tasks = loadTasks();
  applyRolloverRules(tasks, week);
  const w = tasks.weeks[week];
  const today = todayKey(tasks.tz);
  const delta = Math.max(0, daysBetweenKeys(w.startDate, today));
  return Math.min(DAYS_PER_WEEK, delta + 1);
}

export function isWeekCompleted(week) {
  const tasks = loadTasks();
  ensureWeekState(tasks, week);
  const w = tasks.weeks[week];
  return Boolean(w.completeOn);
}

export function canShowNextWeekTaskToday(completedWeek) {
  const tasks = loadTasks();
  ensureWeekState(tasks, completedWeek);
  const w = tasks.weeks[completedWeek];
  if (!w.completeOn) return false;
  const today = todayKey(tasks.tz);
  return daysBetweenKeys(w.completeOn, today) >= 1;
}

export function completeTodayForActiveWeek() {
  const tasks = loadTasks();
  const prog = loadProgress();
  const active = firstIncompleteWeek();
  applyRolloverRules(tasks, active);
  ensureWeekState(tasks, active);
  const w = tasks.weeks[active];

  const today = currentDateKey(tasks);
  const delta = Math.max(0, daysBetweenKeys(w.startDate, today));
  const idx = Math.min(DAYS_PER_WEEK - 1, delta);
  if (w.checks[idx]) return { tasks: saveTasks(tasks), progress: prog, weekCompleted: Boolean(w.completeOn) };
  w.checks[idx] = true;
  w.lastCompletedAt = today;

  // Check if week completed
  const done = w.checks.every(Boolean);
  if (done && !w.completeOn) {
    w.completeOn = today;
    completeCourseWeek(active);
    const updatedP = loadProgress();
    return { tasks: saveTasks(tasks), progress: updatedP, weekCompleted: true, completedWeek: active };
  }

  return { tasks: saveTasks(tasks), progress: prog, weekCompleted: false, lastCompletedAt: w.lastCompletedAt };
}

// New: toggle today's check for the active week (allow undo by unchecking)
export function toggleTodayForActiveWeek(nextChecked) {
  const tasks = loadTasks();
  const progBefore = loadProgress();
  const active = firstIncompleteWeek();
  applyRolloverRules(tasks, active);
  ensureWeekState(tasks, active);
  const w = tasks.weeks[active];

  const today = currentDateKey(tasks);
  const delta = Math.max(0, daysBetweenKeys(w.startDate, today));
  const idx = Math.min(DAYS_PER_WEEK - 1, delta);

  const prev = !!w.checks[idx];
  if (nextChecked === prev) {
    return { tasks: saveTasks(tasks), progress: progBefore, toggledOn: false, toggledOff: false, weekCompleted: Boolean(w.completeOn) };
  }

  if (nextChecked) {
    // set like completeTodayForActiveWeek
    w.checks[idx] = true;
    w.lastCompletedAt = today;
    const done = w.checks.every(Boolean);
    if (done && !w.completeOn) {
      w.completeOn = today;
      completeCourseWeek(active);
      const updatedP = loadProgress();
      return { tasks: saveTasks(tasks), progress: updatedP, toggledOn: true, toggledOff: false, weekCompleted: true };
    }
    return { tasks: saveTasks(tasks), progress: progBefore, toggledOn: true, toggledOff: false, weekCompleted: false };
  } else {
    // uncheck today
    w.checks[idx] = false;
    let uncompleted = false;
    if (w.completeOn) {
      // undo week completion if previously completed
      w.completeOn = null;
      uncompleteWeek(active);
      uncompleted = true;
    }
    return { tasks: saveTasks(tasks), progress: loadProgress(), toggledOn: false, toggledOff: true, weekCompleted: false, uncompletedWeek: uncompleted };
  }
}

export function getDisplayForHome() {
  const active = firstIncompleteWeek();
  const tasks = getWeekTaskState(active);
  const w = tasks.weeks[active];
  const today = todayKey(tasks.tz);
  const delta = Math.max(0, daysBetweenKeys(w.startDate, today));
  const dayIdx = Math.min(DAYS_PER_WEEK, delta + 1);

  const enabled = true; // now allow toggling both on and off for today

  let blockedUntilTomorrow = false;
  if (w.completeOn && daysBetweenKeys(w.completeOn, today) < 1) {
    blockedUntilTomorrow = true;
  }

  const justReset = w.lastResetOn === today;
  return { week: active, dayIdx, checks: w.checks, blockedUntilTomorrow, justReset, enabled };
}

export function getWeekProgress(week) {
  const t = loadTasks();
  applyRolloverRules(t, week);
  ensureWeekState(t, week);
  const w = t.weeks[week];
  const today = todayKey(t.tz);
  const delta = Math.max(0, daysBetweenKeys(w.startDate, today));
  const dayIdx = Math.min(DAYS_PER_WEEK, delta + 1);
  return { checks: [...w.checks], dayIdx, completeOn: w.completeOn, startDate: w.startDate, lastCompletedAt: w.lastCompletedAt || null };
}